const originOf = (url: string | undefined): string | undefined => {
  try {
    return url ? new URL(url).origin : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Is this page being shown inside the Studio's Presentation tool?
 *
 * The wall would sit on top of the page being edited and swallow the editor's clicks. In a frame the
 * cookies are third-party, so a choice made there may not stick and the wall can return on every load.
 *
 * Keyed on the Studio being the embedder rather than on draft mode. The component is shared by both
 * CMS lanes and knows neither which one served it nor whether draft mode is on, and the draft cookie
 * is exactly what a frame may fail to send. What the frame can always tell is who embeds it.
 *
 * Narrow on purpose: only the configured Studio's origin counts. A page embedded anywhere else, or
 * opened on its own, still gets the wall. `ancestorOrigins` is the reliable source; the referrer
 * covers browsers without it on the first load.
 */
export const isEmbeddedByStudio = (studioUrl: string | undefined, win: Window = window): boolean => {
  const studioOrigin = originOf(studioUrl);
  if (!studioOrigin || win.self === win.top) return false;

  // Where the browser reports the chain it is the whole answer: a Studio referrer must not override
  // an embedder that is somebody else.
  const { ancestorOrigins } = win.location;
  if (ancestorOrigins) return Array.from(ancestorOrigins).includes(studioOrigin);
  return originOf(win.document.referrer) === studioOrigin;
};
